import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { fetchCompanyBankAccounts } from "./Trade/bankAccountsFetch";
import { Search, RefreshCcw, Undo2, Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../Product/customers.css";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function money(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function statusFromRow(r) {
  const bal = Number(r?.balance || 0);
  const paid = Number(r?.amount_paid || 0);
  const refunded = Number(r?.amount_refunded || 0);

  if (bal <= 0) return "PAID";
  if (paid - refunded > 0) return "PART PAID";
  return "UNPAID";
}

function badgeClassFromStatus(status) {
  if (status === "PAID") return "badge paid";
  if (status === "PART PAID") return "badge part";
  return "badge open";
}

function SalePaymentModal({
  open,
  onClose,
  companyId,
  sale,
  onPosted,
  bankAccounts,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [type, setType] = useState("PAYMENT");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [bankAccountId, setBankAccountId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setBusy(false);
    setType(sale?.__forceType || "PAYMENT");
    setAmount("");
    setDate(todayISO());
    setNotes("");
    setBankAccountId(sale?.bank_account_id || "");
  }, [open, sale]);

  async function post() {
    setErr("");
    if (!companyId) return setErr("Missing company.");
    if (!sale?.id) return setErr("Missing sale.");
    if (!bankAccountId) return setErr("Select bank account.");
    const amt = Number(amount || 0);
    if (!(amt > 0)) return setErr("Amount must be > 0.");
    if (!date) return setErr("Select date.");

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sales-payment-post`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          sale_id: sale.id,
          type,
          amount: amt,
          payment_date: date,
          bank_account_id: bankAccountId,
          notes: notes?.trim() || null,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }
      if (!res.ok) throw new Error(json?.error || "Failed to post payment.");

      onPosted?.(json);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="sale-payment-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="sale-payment-modal" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="sale-payment-head">
          <div>
            <p className="sale-payment-eyebrow">
              {type === "PAYMENT" ? "Customer Collection" : "Customer Refund"}
            </p>

            <h2>{type === "PAYMENT" ? "Record Payment" : "Record Refund"}</h2>

            <p>
              Record customer cash movement and automatically update receivables
              and bank balances.
            </p>
          </div>

          <button
            className="sale-payment-close"
            onClick={onClose}
            disabled={busy}
          >
            ✕
          </button>
        </div>

        {/* SUMMARY */}
        <div className="sale-payment-summary-grid">
          <div className="sale-payment-summary-card">
            <span>Total Invoice</span>
            <strong>{money(sale?.total)}</strong>
          </div>

          <div className="sale-payment-summary-card">
            <span>Total Paid</span>
            <strong>{money(sale?.amount_paid)}</strong>
          </div>

          <div className="sale-payment-summary-card">
            <span>Total Refunded</span>
            <strong>{money(sale?.amount_refunded)}</strong>
          </div>

          <div className="sale-payment-summary-card">
            <span>Outstanding Balance</span>
            <strong>{money(sale?.balance)}</strong>
          </div>
        </div>

        {/* FORM */}
        <div className="sale-payment-form">
          <div className="sale-payment-grid">
            <div className="sale-payment-field">
              <label>Transaction Type</label>

              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
              >
                <option value="PAYMENT">PAYMENT (money received)</option>

                <option value="REFUND">REFUND (money returned)</option>
              </select>

              <small>
                Select whether this transaction is a customer payment received
                or a refund issued back to the customer.
              </small>
            </div>

            <div className="sale-payment-field">
              <label>Transaction Date</label>

              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={busy}
              />

              <small>
                The accounting date this transaction should reflect in reports
                and ledgers.
              </small>
            </div>
          </div>

          <div className="sale-payment-grid">
            <div className="sale-payment-field">
              <label>Amount</label>

              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={busy}
              />

              <small>
                Enter the exact amount received from or refunded to the
                customer.
              </small>
            </div>

            <div className="sale-payment-field">
              <label>Bank / Cash Account</label>

              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select bank or cash account...</option>

                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.account_number ? `${a.account_number} - ` : ""}
                    {a.name}
                  </option>
                ))}
              </select>

              <small>
                Select the bank or cash account where this payment was received
                or refunded from.
              </small>
            </div>
          </div>

          <div className="sale-payment-field">
            <label>Notes / Narration</label>

            <textarea
              rows={4}
              placeholder="Optional narration or internal note..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />

            <small>
              Add additional details such as transfer reference, payment channel
              or refund explanation.
            </small>
          </div>

          {err && <div className="sale-payment-alert error">{err}</div>}
        </div>

        {/* FOOTER */}
        <div className="sale-payment-footer">
          <button
            className="sale-payment-btn secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>

          <button
            className="sale-payment-btn primary"
            onClick={post}
            disabled={busy}
          >
            {busy
              ? "Posting..."
              : type === "PAYMENT"
              ? "Post Payment"
              : "Post Refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SalesHistory() {
  const { companyId } = useCompany();
  const nav = useNavigate();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const [rows, setRows] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [selected, setSelected] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  async function load() {
    if (!companyId) return;
    setBusy(true);
    setErr("");
    try {
      const { data, error } = await supabase
        .from("v_sales_history")
        .select(
          "id, sale_date, total, payment_status, bank_account_id, amount_paid, amount_refunded, balance, customers(name, company_name)"
        )
        .eq("company_id", companyId)
        .order("sale_date", { ascending: false })
        .limit(200);

      if (error) throw error;
      setRows(data || []);

      const banks = await fetchCompanyBankAccounts(companyId);
      setBankAccounts(banks || []);
    } catch (e) {
      setErr(e?.message || "Failed to load sales.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [companyId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const name = (r.customers?.name || "").toLowerCase();
      const comp = (r.customers?.company_name || "").toLowerCase();
      const date = String(r.sale_date || "");
      return name.includes(s) || comp.includes(s) || date.includes(s);
    });
  }, [q, rows]);

  function openPayment(sale) {
    setSelected({ ...sale, __forceType: "PAYMENT" });
    setShowPayModal(true);
  }
  function openRefund(sale) {
    setSelected({ ...sale, __forceType: "REFUND" });
    setShowPayModal(true);
  }

  // ✅ NEW: Admin-only delete
  async function deleteSale(rowOrId) {
    try {
      if (!companyId) throw new Error("Missing companyId");

      // ✅ Accept either an object row or an id string
      const saleId =
        typeof rowOrId === "string"
          ? rowOrId
          : rowOrId?.id || rowOrId?.sale_id || rowOrId?.sales_transaction_id;

      if (!saleId) {
        // Don’t crash with a noisy stacktrace; show a clear UI message
        console.error("deleteSale called without valid sale id:", rowOrId);
        alert("Cannot delete: sale id is missing on this row. Please refresh.");
        return;
      }

      const ok = window.confirm(
        "Delete this sale? This removes journals and settlements."
      );
      if (!ok) return;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sales-delete`;

      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          sale_id: saleId,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) {
        console.error("sales-delete failed:", {
          status: res.status,
          json,
          companyId,
          saleId,
        });
        throw new Error(json?.error || `Delete failed (${res.status})`);
      }

      await load();
      alert("Sale deleted successfully.");
    } catch (e) {
      console.error("deleteSale error:", e);
      alert(e?.message || "Failed to delete sale");
    }
  }

  return (
    <div className="sales-history-page">
      {/* HERO */}
      <header className="sales-history-hero">
        <div>
          <p className="sales-history-eyebrow">Sales, Collections & Refunds</p>

          <h1>Sales History</h1>

          <p>
            Review all completed sales transactions, track customer payments,
            refunds and outstanding balances.
          </p>
        </div>

        <div className="sales-history-actions">
          <div className="sales-search-wrap">
            <Search size={16} className="sales-search-icon" />

            <input
              type="text"
              placeholder="Search sales by customer or date..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button className="sales-btn soft" onClick={load} disabled={busy}>
            <RefreshCcw size={16} />
            {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </header>

      {err && <div className="sales-alert error">{err}</div>}

      {/* SUMMARY */}
      <section className="sales-summary-grid">
        <div className="sales-summary-card">
          <span>Total Sales</span>
          <strong>{filtered.length}</strong>
        </div>

        <div className="sales-summary-card">
          <span>Total Value</span>
          <strong>
            {money(filtered.reduce((a, b) => a + Number(b.total || 0), 0))}
          </strong>
        </div>

        <div className="sales-summary-card">
          <span>Total Paid</span>
          <strong>
            {money(
              filtered.reduce((a, b) => a + Number(b.amount_paid || 0), 0)
            )}
          </strong>
        </div>

        <div className="sales-summary-card">
          <span>Outstanding</span>
          <strong>
            {money(filtered.reduce((a, b) => a + Number(b.balance || 0), 0))}
          </strong>
        </div>
      </section>

      {/* TABLE */}
      <section className="sales-table-panel">
        <div className="sales-table-head">
          <div>
            <h3>Sales Transactions</h3>

            <p>
              Track customer balances, collections and refunds in real time.
            </p>
          </div>
        </div>

        <div className="sales-table-wrap">
          <table className="sales-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th className="num">Refunded</th>
                <th className="num">Balance</th>
                <th className="center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const st = statusFromRow(r);

                return (
                  <tr key={r.id}>
                    <td>{r.sale_date || "—"}</td>

                    <td>
                      <div className="sales-customer">
                        <strong>{r.customers?.name || "—"}</strong>

                        <span>{r.customers?.company_name || ""}</span>
                      </div>
                    </td>

                    <td>
                      <span className={badgeClassFromStatus(st)}>{st}</span>
                    </td>

                    <td className="num">{money(r.total)}</td>

                    <td className="num">
                      <span className="paid-value">{money(r.amount_paid)}</span>
                    </td>

                    <td className="num">
                      <span className="refund-value">
                        {money(r.amount_refunded)}
                      </span>
                    </td>

                    <td className="num">
                      <span className="balance-value">{money(r.balance)}</span>
                    </td>

                    <td className="center">
                      <div className="sales-row-actions">
                        <button
                          className="sales-action-btn"
                          onClick={() => nav(`/sales/${r.id}`)}
                        >
                          <Eye size={15} />
                          View
                        </button>

                        {Number(r.balance || 0) > 0 && (
                          <button
                            className="sales-action-btn success"
                            onClick={() => openPayment(r)}
                            disabled={busy}
                          >
                            Payment
                          </button>
                        )}

                        <button
                          className="sales-action-btn warning"
                          onClick={() => openRefund(r)}
                          disabled={busy}
                        >
                          <Undo2 size={15} />
                          Refund
                        </button>

                        <button
                          className="sales-action-btn danger"
                          onClick={() => deleteSale(r.id)}
                          disabled={busy}
                        >
                          <Trash2 size={15} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!busy && !filtered.length && (
            <div className="sales-empty">
              <div className="sales-empty-icon">📘</div>

              <h3>No sales found</h3>

              <p>
                Sales transactions will appear here after invoices or sales are
                recorded.
              </p>
            </div>
          )}
        </div>
      </section>

      <SalePaymentModal
        open={showPayModal}
        onClose={() => setShowPayModal(false)}
        companyId={companyId}
        sale={selected}
        bankAccounts={bankAccounts}
        onPosted={() => load()}
      />
    </div>
  );
}
